import React from 'react'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import { Badge } from '@/common/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ChevronDown } from 'lucide-react'

interface GameProviderSelectorProps {
	field: {
		value: string[] | null
		onChange: (value: string[]) => void
	}
	gameProviders:
		| { id: string; name: string; displayName?: string }[]
		| undefined
}

export const PROVIDER_PAIRS: Record<string, string> = {
	PG: 'PGHARD',
	PGHARD: 'PG',
	JOKER: 'JOKERHARD',
	JOKERHARD: 'JOKER',
	JILI: 'HARDV2_JILI',
	HARDV2_JILI: 'JILI',
	PP: 'HARDV2_PRAGMATIC',
	HARDV2_PRAGMATIC: 'PP',
	NLC: 'HARDV2_NOLIMITCITY',
	HARDV2_NOLIMITCITY: 'NLC',
}

export const GameProviderSelector = ({
	field,
	gameProviders,
}: GameProviderSelectorProps) => {
	const [search, setSearch] = React.useState('')
	const filteredProviders = React.useMemo(() => {
		if (!search) return gameProviders
		return gameProviders?.filter((provider) =>
			provider.name.toLowerCase().includes(search.toLowerCase()),
		)
	}, [gameProviders, search])

	const value = field.value || []
	const selectedCount = value.length

	const handleProviderToggle = (providerId: string) => {
		const provider = gameProviders?.find((p) => p.id === providerId)
		if (!provider) return

		const isSelected = value.includes(providerId)
		let newValues: string[]

		if (isSelected) {
			newValues = value.filter((id) => id !== providerId)
		} else {
			newValues = [...value, providerId]
		}

		const pairedProviderName = PROVIDER_PAIRS[provider.name]
		if (pairedProviderName) {
			const pairedProvider = gameProviders?.find(
				(p) => p.name === pairedProviderName,
			)

			if (pairedProvider) {
				if (isSelected) {
					newValues = newValues.filter((id) => id !== pairedProvider.id)
				} else {
					if (!newValues.includes(pairedProvider.id)) {
						newValues = [...newValues, pairedProvider.id]
					}
				}
			}
		}

		field.onChange(newValues)
	}

	return (
		<div className="space-y-2">
			<Popover
				onOpenChange={(open) => {
					if (!open) {
						setSearch('')
					}
				}}
			>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						className="w-full justify-between"
					>
						<div className="flex items-center gap-2 truncate">
							<span className="truncate">
								{selectedCount > 0
									? `${selectedCount} ผู้ให้บริการที่เลือก`
									: 'เลือกผู้ให้บริการเกม'}
							</span>
						</div>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0" align="start">
					<div className="space-y-2 p-2">
						<Input
							placeholder="ค้นหาผู้ให้บริการเกม..."
							className="h-8 text-sm"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<div className="max-h-[300px] space-y-1 overflow-y-auto">
							{filteredProviders?.map((provider) => (
								<div
									key={provider.id}
									className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
									onClick={() => handleProviderToggle(provider.id)}
								>
									<Checkbox
										checked={value.includes(provider.id)}
										className="pointer-events-none h-4 w-4"
									/>
									<span className="text-xs">{provider.name}</span>
								</div>
							))}
							{filteredProviders?.length === 0 && (
								<div className="py-2 text-center text-xs text-muted-foreground">
									ไม่พบผู้ให้บริการเกม
								</div>
							)}
						</div>
						{value.length > 0 && (
							<Button
								variant="ghost"
								className="w-full justify-center text-xs"
								onClick={() => field.onChange([])}
							>
								ล้างทั้งหมด
							</Button>
						)}
					</div>
				</PopoverContent>
			</Popover>

			{value.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{value.map((id) => (
						<Badge
							key={id}
							variant="secondary"
							className="rounded-sm px-2 py-1 text-sm font-normal"
						>
							{gameProviders?.find((provider) => provider.id === id)?.name ||
								id}
						</Badge>
					))}
				</div>
			)}
		</div>
	)
}
